import { localized, msg } from '@lit/localize';
import hljs from 'highlight.js';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import '@spectrum-web-components/icons-workflow/icons/sp-icon-copy.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-volume-mute.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-volume-two.js';

import { emitToast } from '../../../../components/toast-events';
import {
  markdownToPlainText,
  renderMarkdown,
} from '../../../../utils/markdown.js';
import { renderMessageExtras } from './message-common.js';
import {
  formatToolArguments,
  formatToolDuration,
  getToolSourceLabel,
  getToolStatusLabel,
} from './tool-call-utils.js';
import type { ConversationMessage } from './types.js';

export const assistantMessageStyles = css`
  .message .thinking .typing {
    display: inline-flex;
    align-items: center;
    height: 17px;
  }
  .message .thinking .dot {
    display: inline-block;
    width: 5px;
    height: 5px;
    margin-right: 4px;
    border-radius: 50%;
    vertical-align: middle;
    background: var(--color-chat-placeholder-text);
    opacity: 0.55;
    animation: mercury-typing 1.8s infinite ease-in-out;
  }
  .message .thinking .dot:nth-child(1) {
    animation-delay: 200ms;
  }
  .message .thinking .dot:nth-child(2) {
    animation-delay: 300ms;
  }
  .message .thinking .dot:nth-child(3) {
    margin-right: 0;
    animation-delay: 400ms;
  }
  @keyframes mercury-typing {
    0%,
    44%,
    100% {
      transform: translateY(0);
      opacity: 0.45;
    }
    28% {
      transform: translateY(-7px);
      opacity: 0.9;
    }
  }

  .message .message-actions {
    margin-top: var(--gap-1);
    display: flex;
    align-items: center;
    gap: 0;
  }
  .message .message-actions .copy-message,
  .message .message-actions .speak-message {
    margin: 0;
  }
  .message .copy-message {
    border: none;
    padding: 0;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background: transparent;
    color: var(--color-chat-placeholder-text);
    opacity: 0.7;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: left;
    transition: opacity 0.15s ease, color 0.15s ease;
  }
  .message .copy-message:hover {
    opacity: 1;
    color: var(--color-chat-text);
  }
  .message .copy-message sp-icon-copy {
    width: 14px;
    height: 14px;
    pointer-events: none;
  }

  .message .speak-message {
    border: none;
    padding: 0;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background: transparent;
    color: var(--color-chat-placeholder-text);
    opacity: 0.7;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: left;
    transition: opacity 0.15s ease, color 0.15s ease;
  }
  .message .speak-message:hover {
    opacity: 1;
    color: var(--color-chat-text);
  }
  .message .speak-message.speaking {
    opacity: 1;
    color: var(--color-chat-text);
  }
  .message .speak-message sp-icon-volume-two,
  .message .speak-message sp-icon-volume-mute {
    width: 14px;
    height: 14px;
    pointer-events: none;
  }
  .message .tool-calls {
    margin-top: 12px;
    margin-bottom: 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 100%;
  }
  .message .tool-call {
    border: 1px solid var(--color-app-border, rgba(127, 127, 127, 0.22));
    border-radius: 8px;
    background: var(--color-sidebar-surface, rgba(127, 127, 127, 0.06));
    box-shadow: none;
    overflow: hidden;
  }
  .message .tool-call[open] {
    background: var(--color-sidebar-surface, rgba(127, 127, 127, 0.08));
  }
  .message .tool-call summary {
    cursor: pointer;
    list-style: none;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
    align-items: start;
    padding: 10px 12px;
    color: var(--color-chat-text);
  }
  .message .tool-call summary::-webkit-details-marker {
    display: none;
  }
  .message .tool-summary-main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .message .tool-title {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    line-height: 1.3;
  }
  .message .tool-title::before {
    width: 7px;
    height: 7px;
    flex: 0 0 auto;
    border-radius: 999px;
    background: var(--color-accent);
    content: '';
  }
  .message .tool-name {
    font-weight: 700;
    overflow-wrap: anywhere;
  }
  .message .tool-meta {
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    line-height: 1.25;
  }
  .message .tool-server,
  .message .tool-source,
  .message .tool-duration {
    border-radius: 999px;
    background: var(--color-sidebar-bg, rgba(127, 127, 127, 0.1));
    color: var(--color-chat-placeholder-text);
    padding: 3px 7px;
  }
  .message .tool-status {
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    background: var(--color-sidebar-bg, rgba(127, 127, 127, 0.14));
    color: var(--color-chat-placeholder-text);
  }
  .message .tool-status.running {
    background: var(--color-sidebar-selected-bg);
    color: var(--color-accent);
  }
  .message .tool-status.succeeded {
    background: var(--color-success-bg, rgba(38, 142, 108, 0.12));
    color: var(--color-success-text, var(--spectrum-green-700, #268e6c));
  }
  .message .tool-status.failed {
    background: var(--color-error-bg, rgba(215, 55, 63, 0.12));
    color: var(--color-error-text, var(--spectrum-red-700, #d7373f));
  }
  .message .tool-body {
    border-top: 1px solid var(--color-app-border, rgba(127, 127, 127, 0.18));
    background: transparent;
    padding: 10px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .message .tool-section-title {
    margin-bottom: 4px;
    color: var(--color-chat-placeholder-text);
    font-size: 11px;
    font-weight: 700;
  }
  .message .tool-arguments,
  .message .tool-error {
    margin: 0;
    padding: 10px;
    border: 1px solid var(--color-app-border, rgba(127, 127, 127, 0.18));
    border-radius: 7px;
    background: var(--color-sidebar-surface, rgba(127, 127, 127, 0.05));
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    font-family: var(--mono, ui-monospace, SFMono-Regular, Menlo, monospace);
    font-size: 12.5px;
    line-height: 1.5;
  }
  .message .tool-arguments code,
  .message .tool-error code {
    background: transparent;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    padding: 0;
  }
  .message .tool-arguments .hljs-attr {
    color: var(--color-syntax-attr);
  }
  .message .tool-arguments .hljs-string {
    color: var(--color-syntax-string);
  }
  .message .tool-arguments .hljs-number {
    color: var(--color-syntax-number);
  }
  .message .tool-arguments .hljs-literal {
    color: var(--color-syntax-literal);
  }
  .message .tool-arguments .hljs-punctuation {
    color: var(--color-syntax-punctuation);
  }
  .message .tool-error {
    color: var(--color-error-text, var(--spectrum-red-700, #d7373f));
  }

  @media (max-width: 768px) {
    .message .copy-message,
    .message .speak-message {
      width: 44px;
      height: 44px;
    }
  }
`;

@customElement('kv-chat-assistant-message')
@localized()
export class KvChatAssistantMessage extends LitElement {
  @property({ attribute: false, hasChanged: () => true })
  accessor message: ConversationMessage | null = null;

  @property({ type: Boolean })
  accessor thinking = false;

  @state()
  private accessor speaking = false;

  private _utteranceId = 0;

  private renderThinkingIndicator() {
    return html`
      <div class="thinking" role="status" aria-label=${msg('Assistant is thinking')}>
        <div class="typing" aria-hidden="true">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    `;
  }

  private renderMessageActions() {
    return html`
      <div class="message-actions">
        ${
          typeof window !== 'undefined' && window.speechSynthesis
            ? html`
                <button
                  class="speak-message ${this.speaking ? 'speaking' : ''}"
                  type="button"
                  @click=${this.onSpeak}
                  aria-label=${this.speaking ? msg('Stop reading') : msg('Read aloud')}
                  title=${this.speaking ? msg('Stop reading') : msg('Read aloud')}
                >
                  ${
                    this.speaking
                      ? html`<sp-icon-volume-mute></sp-icon-volume-mute>`
                      : html`<sp-icon-volume-two></sp-icon-volume-two>`
                  }
                </button>
              `
            : nothing
        }
        <button
          class="copy-message"
          type="button"
          @click=${this.onCopy}
          aria-label=${msg('Copy message')}
          title=${msg('Copy message')}
        >
          <sp-icon-copy></sp-icon-copy>
        </button>
      </div>
    `;
  }

  private escapeHtml(value: string) {
    return value.replace(
      /[&<>"']/g,
      (char) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#039;',
        })[char] ?? char
    );
  }

  private highlightJson(jsonString: string): string {
    try {
      return hljs.highlight(jsonString, { language: 'json' }).value;
    } catch {
      return this.escapeHtml(jsonString);
    }
  }

  private renderToolCalls(message: ConversationMessage) {
    const toolCalls = message.toolCalls ?? [];
    if (!toolCalls.length) {
      return nothing;
    }

    return html`
      <div class="tool-calls">
        ${toolCalls.map((toolCall) => {
          const duration = formatToolDuration(toolCall.durationMs);
          const args = formatToolArguments(toolCall.arguments);
          return html`
            <details class="tool-call">
              <summary>
                <span class="tool-summary-main">
                  <span class="tool-title">
                    <span class="tool-name">${toolCall.displayName}</span>
                  </span>
                  <span class="tool-meta">
                    <span class="tool-source">${getToolSourceLabel(toolCall)}</span>
                    ${
                      toolCall.serverName
                        ? html`<span class="tool-server">${toolCall.serverName}</span>`
                        : nothing
                    }
                    ${
                      duration
                        ? html`<span class="tool-duration">${duration}</span>`
                        : nothing
                    }
                  </span>
                </span>
                <span class="tool-status ${toolCall.status}">
                  ${getToolStatusLabel(toolCall.status)}
                </span>
              </summary>
              ${
                args || toolCall.error
                  ? html`
                    <div class="tool-body">
                      ${
                        args
                          ? html`<div>
                              <div class="tool-section-title">${msg('Arguments')}</div>
                              <pre class="tool-arguments hljs"><code>${unsafeHTML(
                                this.highlightJson(args)
                              )}</code></pre>
                            </div>`
                          : nothing
                      }
                      ${
                        toolCall.error
                          ? html`<div>
                              <div class="tool-section-title">${msg('Error')}</div>
                              <pre class="tool-error">${toolCall.error}</pre>
                            </div>`
                          : nothing
                      }
                    </div>
                  `
                  : nothing
              }
            </details>
          `;
        })}
      </div>
    `;
  }

  protected createRenderRoot(): this {
    return this;
  }

  disconnectedCallback(): void {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    super.disconnectedCallback();
  }

  private renderMessageContent(message: ConversationMessage) {
    if (this.thinking) {
      return this.renderThinkingIndicator();
    }

    const markdown = renderMarkdown(message.content);
    if (!markdown) {
      return message.content;
    }

    return html`<div class="markdown">${unsafeHTML(markdown)}</div>`;
  }

  private copyMessageContentFallback(content: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = content;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    if (!copied) {
      throw new Error('Clipboard API not available');
    }
  }

  private async onCopy(): Promise<void> {
    const rawContent = this.message?.content?.trim() ?? '';
    if (!rawContent) return;

    const copyContent = markdownToPlainText(rawContent);
    if (!copyContent) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(copyContent);
      } else {
        this.copyMessageContentFallback(copyContent);
      }
      emitToast(this, {
        variant: 'success',
        message: msg('Message copied'),
      });
    } catch (error) {
      console.error('Failed to copy chat message', error);
      emitToast(this, {
        variant: 'error',
        message: msg('Failed to copy message'),
      });
    }
  }

  private onSpeak(): void {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;

    const rawContent = this.message?.content?.trim() ?? '';
    const plainText = rawContent ? markdownToPlainText(rawContent)?.trim() : '';
    if (!plainText) return;

    if (this.speaking) {
      window.speechSynthesis.cancel();
      this.speaking = false;
      return;
    }

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(plainText);
    const utteranceId = ++this._utteranceId;

    const onEnd = (): void => {
      if (utteranceId === this._utteranceId) {
        this.speaking = false;
      }
    };

    utterance.addEventListener('end', onEnd);
    utterance.addEventListener('error', onEnd);

    this.speaking = true;
    window.speechSynthesis.speak(utterance);
  }

  render() {
    const message = this.message;
    if (!message) {
      return nothing;
    }
    const hasContent = message.content.trim().length > 0;

    return html`
      <div class="message assistant">
        ${this.renderToolCalls(message)}
        <div class="content">${this.renderMessageContent(message)}</div>
        ${renderMessageExtras(message)}
        ${this.thinking || !hasContent ? nothing : this.renderMessageActions()}
      </div>
    `;
  }
}
